import React from "react";
import AvatarChip from "./ui/avatar-chip";
import StatusChip from "./ui/status-chip";
import SwipeRow from "./ui/swipe-row";
import useIsMobile from "../hooks/useIsMobile";

/**
 * TodayRow — the uniform row primitive for /me.
 *
 * Props:
 *   kind: "priority" | "task" | "jot" | "brief" | "review" | "team-member"
 *   text: row title
 *   done: boolean (strikethrough + fade)
 *   onToggleDone: (next: boolean) => void
 *   onClick: () => void   (row click — usually navigates)
 *   metadata: React node or array of node parts (shown below text)
 *   rightBadge: React node (right-aligned pill/date)
 *   actions: [{label, onClick, destructive?}]
 *   isCursor: boolean (keyboard selection highlight)
 *
 * Kind determines icon color + icon. Jot = no icon. Task = no icon (checkbox only).
 */
const KIND_ICON = {
  priority: { glyph: "★", color: "var(--a)", bg: "rgba(245,158,11,.14)" },
  brief:    { glyph: "▤", color: "#a78bfa",  bg: "rgba(167,139,250,.14)" },
  review:   { glyph: "◉", color: "var(--a)", bg: "rgba(245,158,11,.14)" },
  project:  { glyph: "◉", color: "#60a5fa",  bg: "rgba(96,165,250,.14)" },
  "team-member": { glyph: "◯", color: "#5eead4", bg: "rgba(94,234,212,.1)" },
};

export default function TodayRow({
  kind = "task",
  text,
  done = false,
  onToggleDone,
  onClick,
  metadata = null,
  rightBadge = null,
  actions = [],
  isCursor = false,
  className = "",
  swipeRight,  // { action: () => void, hint: string }
  swipeLeft,   // { action: () => void, hint: string }
  // Drag-to-reorder (caller supplies handlers when draggable)
  draggable = false,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  isDragging = false,
  isDragTarget = false,
  // Inline title edit (caller supplies onSaveText when allowed)
  onSaveText,
}) {
  const isMobile = useIsMobile();
  const icon = KIND_ICON[kind];
  const showCheck = kind !== "brief" && kind !== "project" && kind !== "team-member";

  // Inline-edit state for the row title
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(text || "");
  const [saving, setSaving] = React.useState(false);
  const inputRef = React.useRef(null);
  React.useEffect(() => { setDraft(text || ""); }, [text]);
  React.useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      try { inputRef.current.select(); } catch (_) {}
    }
  }, [editing]);

  const commitEdit = async () => {
    if (!onSaveText) { setEditing(false); return; }
    const next = (draft || "").trim();
    if (next === (text || "").trim()) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSaveText(next);
      setEditing(false);
    } catch (_) {
      // Parent toasts the error; keep the input open for retry
    } finally {
      setSaving(false);
    }
  };
  const cancelEdit = () => { setDraft(text || ""); setEditing(false); };

  const rowClick = (e) => {
    // Don't trigger onClick if check, action buttons, or inline editor swallowed it
    if (e.target.closest("[data-row-check]") || e.target.closest("[data-row-action]") || e.target.closest("[data-row-edit]")) return;
    if (editing) return;
    if (onClick) onClick();
  };

  const rowEl = (
    <div
      onClick={rowClick}
      draggable={draggable && !editing}
      onDragStart={draggable && !editing ? onDragStart : undefined}
      onDragOver={draggable ? onDragOver : undefined}
      onDrop={draggable ? onDrop : undefined}
      onDragEnd={draggable ? onDragEnd : undefined}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        padding: "11px 12px 11px 8px",
        borderRadius: 12,
        transition: "background .15s, outline .15s, opacity .15s",
        position: "relative",
        cursor: editing ? "text" : (draggable ? "grab" : (onClick ? "pointer" : "default")),
        marginBottom: 2,
        background: isCursor ? "rgba(96,165,250,.08)" : (isDragTarget ? "rgba(96,165,250,.12)" : "transparent"),
        outline: isCursor ? "1px solid rgba(96,165,250,.3)" : (isDragTarget ? "2px dashed rgba(96,165,250,.5)" : "none"),
        opacity: isDragging ? 0.4 : 1,
      }}
      className={`dm-today-row ${className}`}
      onMouseEnter={(e) => {
        if (!isCursor && !isDragTarget) e.currentTarget.style.background = "var(--s1)";
      }}
      onMouseLeave={(e) => {
        if (!isCursor && !isDragTarget) e.currentTarget.style.background = "transparent";
      }}
    >
      {/* Icon slot */}
      {icon ? (
        <div
          style={{
            width: 24,
            height: 24,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 15,
            flexShrink: 0,
            color: icon.color,
            background: icon.bg,
            borderRadius: 6,
            marginTop: 1,
          }}
          aria-hidden
        >
          {icon.glyph}
        </div>
      ) : (
        <div style={{ width: 24, height: 24, flexShrink: 0 }} aria-hidden />
      )}

      {/* Checkbox */}
      {showCheck ? (
        <button
          data-row-check
          type="button"
          onClick={(e) => { e.stopPropagation(); if (onToggleDone) onToggleDone(!done); }}
          aria-label={done ? "Mark not done" : "Mark done"}
          style={{
            width: 22,
            height: 22,
            borderRadius: "50%",
            border: `1.5px solid ${done ? "var(--g)" : "var(--t4, #3e3e3e)"}`,
            background: done ? "var(--g)" : "transparent",
            flexShrink: 0,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all .18s",
            marginTop: 2,
            padding: 0,
            fontSize: 12,
            color: done ? "#0b0b0d" : "transparent",
            fontWeight: 700,
            lineHeight: 1,
          }}
          onMouseEnter={(e) => {
            if (!done) {
              e.currentTarget.style.borderColor = "var(--g)";
              e.currentTarget.style.background = "rgba(34,197,94,.1)";
            }
          }}
          onMouseLeave={(e) => {
            if (!done) {
              e.currentTarget.style.borderColor = "var(--t4, #3e3e3e)";
              e.currentTarget.style.background = "transparent";
            }
          }}
        >
          {done ? "✓" : ""}
        </button>
      ) : (
        <div style={{ width: 22, height: 22, flexShrink: 0 }} aria-hidden />
      )}

      {/* Body */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {editing && onSaveText ? (
          <input
            ref={inputRef}
            data-row-edit
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); commitEdit(); }
              else if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
            }}
            onClick={(e) => e.stopPropagation()}
            disabled={saving}
            style={{
              width: "100%",
              background: "var(--bg)",
              border: "1px solid #60a5fa",
              borderRadius: 8,
              padding: "4px 8px",
              color: "var(--t1)",
              fontFamily: "var(--font-sans)",
              fontSize: 16,
              fontWeight: kind === "priority" || kind === "task" ? 500 : 400,
              lineHeight: 1.35,
              letterSpacing: "-0.005em",
              outline: "none",
              boxShadow: "0 0 0 3px rgba(96,165,250,.15)",
            }}
          />
        ) : (
          <div
            style={{
              fontSize: 16,
              fontWeight: kind === "priority" || kind === "task" ? 500 : 400,
              color: done ? "var(--t3)" : "var(--t1)",
              lineHeight: 1.35,
              letterSpacing: "-0.005em",
              wordWrap: "break-word",
              textDecoration: done ? "line-through" : "none",
              cursor: onSaveText ? "text" : undefined,
            }}
            className="dm-today-row-title"
            onClick={(e) => {
              // If inline-edit is enabled, clicking the text enters edit mode.
              // Otherwise the click bubbles to the row's onClick.
              if (onSaveText && !done) {
                e.stopPropagation();
                setEditing(true);
              }
            }}
            title={onSaveText ? "Click to rename" : undefined}
          >
            {text}
          </div>
        )}
        {metadata && (
          <div
            style={{
              marginTop: 3,
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 13,
              color: "var(--t3)",
              fontWeight: 400,
              lineHeight: 1.3,
              flexWrap: "wrap",
            }}
            className="dm-today-row-meta"
          >
            {metadata}
          </div>
        )}
      </div>

      {/* Right-side badge */}
      {rightBadge && (
        <div
          style={{
            fontSize: 12,
            color: "var(--t3)",
            flexShrink: 0,
            fontWeight: 500,
            alignSelf: "center",
            marginLeft: 4,
          }}
          className="dm-today-row-right"
        >
          {rightBadge}
        </div>
      )}

      {/* Hover actions */}
      {actions && actions.length > 0 && (
        <div
          className="dm-today-row-actions"
          style={{
            display: "flex",
            gap: 4,
            flexShrink: 0,
            alignSelf: "center",
          }}
        >
          {actions.map((a, i) => (
            <button
              key={i}
              data-row-action
              type="button"
              onClick={(e) => { e.stopPropagation(); a.onClick?.(); }}
              style={{
                background: "transparent",
                border: "none",
                padding: "6px 10px",
                borderRadius: 8,
                color: a.destructive ? "var(--t3)" : "var(--t3)",
                fontFamily: "var(--font-sans)",
                fontSize: 12,
                fontWeight: 500,
                cursor: "pointer",
                transition: "all .15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "var(--s3)";
                e.currentTarget.style.color = a.destructive ? "var(--r)" : "var(--t1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = "var(--t3)";
              }}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // On mobile, wrap in SwipeRow if the caller provided swipe actions
  if (isMobile && (swipeRight || swipeLeft)) {
    return (
      <SwipeRow
        onSwipeRight={swipeRight?.action}
        onSwipeLeft={swipeLeft?.action}
        rightHint={swipeRight?.hint}
        leftHint={swipeLeft?.hint}
      >
        {rowEl}
      </SwipeRow>
    );
  }
  return rowEl;
}

/** Small helpers for building common metadata pieces */
TodayRow.Avatar = AvatarChip;
TodayRow.Status = StatusChip;
TodayRow.Sep = function Sep() {
  return <span style={{ color: "var(--t4, #3e3e3e)" }}>·</span>;
};
TodayRow.Overdue = function Overdue({ children }) {
  return <span style={{ color: "var(--r)", fontWeight: 500 }}>{children}</span>;
};
TodayRow.Today = function Today({ children }) {
  return <span style={{ color: "var(--a)", fontWeight: 500 }}>{children}</span>;
};
TodayRow.Soon = function Soon({ children }) {
  return <span style={{ color: "#60a5fa" }}>{children}</span>;
};
